/**
 * Region-first conversion geometry (ADR-0006).
 *
 * The photo canvas is displayed with CSS letterboxing (`object-fit: contain`
 * style fitting) inside a viewport. The user draws a rectangle in *viewport*
 * pixels; this module maps that rectangle to native source-image pixel
 * coordinates and clamps it to the image bounds. Pure math — no DOM — so the
 * mapping is unit-testable and identical between the marquee overlay and the
 * crop call.
 */

export interface ViewportFit {
  /** CSS box of the rendered image inside the viewport, in viewport px. */
  offsetX: number;
  offsetY: number;
  /** Rendered size of the full image, in viewport px. */
  displayWidth: number;
  displayHeight: number;
  /** Native source image size, in source px. */
  sourceWidth: number;
  sourceHeight: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fit a source image into a viewport box preserving aspect ratio
 * (contain). Matches the CSS `object-fit: contain` letterboxing.
 */
export function computeContainFit(
  sourceWidth: number,
  sourceHeight: number,
  viewportWidth: number,
  viewportHeight: number
): ViewportFit {
  if (sourceWidth <= 0 || sourceHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return {
      offsetX: 0,
      offsetY: 0,
      displayWidth: 0,
      displayHeight: 0,
      sourceWidth: Math.max(0, sourceWidth),
      sourceHeight: Math.max(0, sourceHeight),
    };
  }
  const scale = Math.min(viewportWidth / sourceWidth, viewportHeight / sourceHeight);
  const displayWidth = sourceWidth * scale;
  const displayHeight = sourceHeight * scale;
  return {
    offsetX: (viewportWidth - displayWidth) / 2,
    offsetY: (viewportHeight - displayHeight) / 2,
    displayWidth,
    displayHeight,
    sourceWidth,
    sourceHeight,
  };
}

/**
 * Map a selection rectangle expressed in viewport pixels onto native source
 * image pixels. The rectangle is clamped to the displayed image area first,
 * so a drag that strays into the letterbox gutter never produces negative or
 * out-of-range crop coordinates. Returns null when the visible overlap is
 * degenerate (a tap, or a drag entirely in the gutter).
 */
export function viewportRectToSourceRect(
  fit: ViewportFit,
  rect: Rect,
  minSourceSize = 4
): Rect | null {
  if (fit.displayWidth <= 0 || fit.displayHeight <= 0) return null;

  // Clamp the rect to the displayed image area.
  const left = Math.max(rect.x, fit.offsetX);
  const top = Math.max(rect.y, fit.offsetY);
  const right = Math.min(rect.x + rect.width, fit.offsetX + fit.displayWidth);
  const bottom = Math.min(rect.y + rect.height, fit.offsetY + fit.displayHeight);
  if (right <= left || bottom <= top) return null;

  const scaleX = fit.sourceWidth / fit.displayWidth;
  const scaleY = fit.sourceHeight / fit.displayHeight;

  let x = Math.round((left - fit.offsetX) * scaleX);
  let y = Math.round((top - fit.offsetY) * scaleY);
  let width = Math.round((right - left) * scaleX);
  let height = Math.round((bottom - top) * scaleY);

  // Clamp to source bounds (rounding can push the far edge one pixel over).
  x = Math.max(0, Math.min(x, fit.sourceWidth - 1));
  y = Math.max(0, Math.min(y, fit.sourceHeight - 1));
  width = Math.max(0, Math.min(width, fit.sourceWidth - x));
  height = Math.max(0, Math.min(height, fit.sourceHeight - y));

  if (width < minSourceSize || height < minSourceSize) return null;
  return { x, y, width, height };
}

// ---------------------------------------------------------------------------
// Editable selection geometry (VS-SLICE-8). The user must be able to reshape
// the box until it frames exactly what they want; the crop contract stays the
// same, only the rect changes. All functions are pure and clamped so a drag can
// never invert the box or push it across its own opposite edge.
// ---------------------------------------------------------------------------

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export function resizeRect(
  rect: Rect,
  handle: ResizeHandle,
  pointer: { x: number; y: number },
  minSize = 8
): Rect {
  let left = rect.x;
  let top = rect.y;
  let right = rect.x + rect.width;
  let bottom = rect.y + rect.height;

  if (handle.includes('w')) left = Math.min(pointer.x, right - minSize);
  if (handle.includes('e')) right = Math.max(pointer.x, left + minSize);
  if (handle.includes('n')) top = Math.min(pointer.y, bottom - minSize);
  if (handle.includes('s')) bottom = Math.max(pointer.y, top + minSize);

  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function moveRect(rect: Rect, dx: number, dy: number): Rect {
  return { x: rect.x + dx, y: rect.y + dy, width: rect.width, height: rect.height };
}

/**
 * Grow a rect to at least `minSide` on both axes around its center, clamped to
 * the image bounds. The server rejects crops below 128 px (model resolution
 * contract), so a proposal or hand-drawn box that is too small must be grown
 * before it is used as a conversion input.
 */
export function expandRectToMinSide(
  rect: Rect,
  minSide: number,
  sourceWidth: number,
  sourceHeight: number
): Rect {
  let width = Math.max(rect.width, minSide);
  let height = Math.max(rect.height, minSide);
  width = Math.min(width, sourceWidth);
  height = Math.min(height, sourceHeight);

  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  let x = Math.round(centerX - width / 2);
  let y = Math.round(centerY - height / 2);
  x = Math.max(0, Math.min(x, sourceWidth - width));
  y = Math.max(0, Math.min(y, sourceHeight - height));

  return { x, y, width: Math.round(width), height: Math.round(height) };
}
